import { signal, type Signal } from '@preact/signals'
import type { Client } from '../../../client/protocol.ts'
import type { ActionKind, ActionParams, ActionRecord, SessionSnapshot } from '../../../protocol/views.ts'
import { act, describe } from '../actions.ts'
import type { PageOwner } from '../shell/owner.ts'
import { watch } from '../store/watch.ts'
import type { MenuItem } from '../ui/overlays.tsx'
import type { Feedback } from '../ui/said.tsx'
import type { PageQuery, SessionRow, SessionsCapabilities } from './contracts.ts'

interface Observation {
  producerGeneration: string | undefined
  turnId: string | null
  stop?: 'stop' | 'interrupt'
}
export interface RowMenuObservation {
  readonly row: Readonly<SessionRow>
  readonly workspace: boolean
  readonly observed: Signal<Readonly<Observation> | undefined>
  readonly ready: Promise<Readonly<Observation> | undefined>
  dispose(): void
}
type Intent = 'mark-read' | 'keep' | 'remove' | 'stop' | 'copy'

/** Future core commands cross this one boundary, only behind their named capability. */
const additiveAction = (kind: 'session.keep' | 'session.unkeep' | 'readpoint.markAll', target: string | undefined, params: object) =>
  act(kind as ActionKind, target, params as ActionParams[ActionKind])

export class SessionActions {
  private client: Client
  private owner: PageOwner
  private feedback: Feedback
  private capabilities: SessionsCapabilities
  constructor(client: Client, owner: PageOwner, feedback: Feedback, capabilities: SessionsCapabilities) {
    this.client = client; this.owner = owner; this.feedback = feedback; this.capabilities = capabilities
  }
  /** One inspect lease per opening; the first observation is frozen, never refreshed at click. */
  open(row: SessionRow, workspace = true): RowMenuObservation {
    const displayed = Object.freeze({ ...row, ...(row.readBoundary ? { readBoundary: Object.freeze({ ...row.readBoundary }) } : {}) })
    const observed = signal<Readonly<Observation>>()
    let stopped = false, settled = false, stop = () => {}, activity = () => {}, release = () => {}, resolve!: (value: Readonly<Observation> | undefined) => void
    const ready = new Promise<Readonly<Observation> | undefined>(done => { resolve = done })
    const dispose = () => { if (stopped) return; stopped = true; stop(); activity(); release(); if (!settled) { settled = true; resolve(undefined) } }
    const receive = (snapshot: SessionSnapshot) => {
      if (stopped || settled) return
      const available = snapshot.capabilities.available
      observed.value = Object.freeze({ producerGeneration: snapshot.producerGeneration, turnId: snapshot.live.turnId ?? null,
        ...(!snapshot.live.incomplete && snapshot.producerGeneration && snapshot.live.turnId && (snapshot.live.state === 'running' || snapshot.live.state === 'waiting')
          ? available.includes('stop') ? { stop: 'stop' as const } : available.includes('interrupt.main') ? { stop: 'interrupt' as const } : {} : {}) })
      settled = true; resolve(observed.value); dispose()
    }
    if (!this.owner.active || this.owner.disposed) dispose()
    else stop = watch(this.owner, this.client, 'session', { ref: row.ref, inspect: true }, { onSnapshot: receive, onPatch() {}, onReset() {}, onError: dispose })
    // Synchronous stand-ins may have supplied their snapshot before watch returned.
    if (stopped) stop()
    else { activity = this.owner.onActive(active => { if (!active) dispose() }); release = this.owner.own(dispose) }
    return { row: displayed, workspace, observed, ready, dispose }
  }
  entries(menu: RowMenuObservation): MenuItem[] {
    const items: MenuItem[] = [], entry = (id: Intent, label: string, destructive = false) => ({ id, label, destructive, run: () => { void this.run(id, menu) } })
    if (this.capabilities.readBoundary && menu.row.readBoundary) items.push(entry('mark-read', 'Mark read'))
    if (this.capabilities.keep) items.push(menu.workspace || menu.row.kept ? entry('remove', 'Remove from Workspace', true) : entry('keep', 'Keep in Workspace'))
    if (menu.observed.value?.stop) items.push(entry('stop', 'Stop', true))
    items.push(entry('copy', menu.row.handle ? 'Copy handle' : 'Copy reference'))
    return items
  }
  async run(intent: Intent, menu: RowMenuObservation) {
    if (!this.owner.active || this.owner.disposed) return
    const { row } = menu
    if (intent === 'copy') { await this.feedback.copy(row.handle ?? row.ref); return }
    if (intent === 'mark-read') {
      if (!this.capabilities.readBoundary || !row.readBoundary) return
      const record = await act('mark-read', row.ref, { ...row.readBoundary })
      this.result(record, 'Marked read. Undo restores the read point; it does not restore archived sessions.', () => this.undo(record)); return
    }
    if (intent === 'keep' || intent === 'remove') {
      if (!this.capabilities.keep) return
      const record = await additiveAction(intent === 'keep' ? 'session.keep' : 'session.unkeep', row.ref, {})
      this.result(record, intent === 'keep' ? 'Kept in Workspace.' : 'Removed from Workspace.', intent === 'remove' ? async () => {
        this.result(await additiveAction('session.keep', row.ref, {}), 'Restored to Workspace.')
      } : undefined); return
    }
    const observed = menu.observed.peek()
    if (!observed?.stop) return
    const params = { producerGeneration: observed.producerGeneration, turnId: observed.turnId }
    const record = observed.stop === 'stop' ? await act('stop', row.ref, params) : await act('interrupt', row.ref, { ...params, turnId: observed.turnId! })
    this.result(record, 'Stopped.')
  }
  async markAll(filter: PageQuery) {
    if (!this.capabilities.bulkRead || !this.owner.active || this.owner.disposed) return
    const record = await additiveAction('readpoint.markAll', undefined, { filter: { ...filter } })
    const count = record.result && 'count' in record.result && typeof record.result.count === 'number' ? record.result.count : undefined
    this.result(record, count === undefined ? 'Marked read. Undo restores read points; it does not restore archived sessions.' : `Marked ${count} read. Undo restores read points; it does not restore archived sessions.`, () => this.undo(record))
  }
  private async undo(record: ActionRecord) {
    const result = await act('undo', record.target, { actionId: record.id })
    const restored = result.result && 'restored' in result.result ? result.result.restored : undefined
    const skipped = result.result && 'skipped' in result.result ? result.result.skipped : undefined
    this.result(result, typeof restored === 'number' && typeof skipped === 'number' ? `Restored ${restored} · ${skipped} had changed.` : 'Read point restored.')
  }
  private result(record: ActionRecord, success: string, undo?: () => Promise<void>) {
    if (this.owner.disposed || !this.owner.active) return
    let used = false
    if (record.state === 'confirmed') this.feedback.say(success, undo ? 'undo' : 'note', undo ? { label: 'Undo', run: () => { if (!used) { used = true; this.feedback.clear(); void undo() } } } : undefined)
    else if (record.state === 'accepted' || record.state === 'executing') this.feedback.say('Action in progress.', 'note')
    else this.feedback.say(record.state === 'unknown' ? 'Outcome unknown. Check the session before trying again.' : describe(record), 'error')
  }
}
